import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LANGS, setDictEntry } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Undo2, Loader2 } from "lucide-react";

const LANG_NAME = Object.fromEntries(LANGS);
const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

export const TranslationHistoryTab = () => {
  const [rows, setRows] = useState(null);
  const [lang, setLang] = useState("");
  const [by, setBy] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/i18n/history").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const authors = useMemo(() => [...new Set((rows || []).map((r) => r.by).filter(Boolean))].sort(), [rows]);
  const shown = useMemo(() => (rows || []).filter((r) => (!lang || r.lang === lang) && (!by || r.by === by)
    && (!q || [r.key, r.old, r.new].some((s) => (s || "").toLowerCase().includes(q.toLowerCase())))), [rows, lang, by, q]);

  const revert = async (r) => {
    setBusy(r.id);
    try {
      await api.put("/i18n", { lang: r.lang, key: r.key, text: r.old });
      setDictEntry(r.lang, r.key, r.old);
      toast.success("Ancienne traduction rétablie");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="translation-history-tab">
      <div className="flex flex-wrap items-center gap-2">
        <select value={lang} onChange={(e) => setLang(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="tr-history-lang">
          <option value="">Toutes les langues</option>
          {LANGS.filter(([c]) => c !== "fr").map(([c, n]) => <option key={c} value={c}>{n}</option>)}
        </select>
        <select value={by} onChange={(e) => setBy(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="tr-history-author">
          <option value="">Toutes les personnes</option>
          {authors.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un texte…" className="h-9 w-64" data-testid="tr-history-search" />
        <span className="text-xs text-muted-foreground ml-auto" data-testid="tr-history-count">{shown.length} correction(s)</span>
      </div>
      {rows === null ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> : (
        <div className="space-y-2">
          {shown.map((r) => (
            <div key={r.id} className="rounded-lg border border-border bg-card p-3 text-sm" data-testid={`tr-history-row-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-2">
                <span className="font-medium text-foreground">{r.by || "—"}</span>
                <span>·</span><span>{LANG_NAME[r.lang] || r.lang}</span>
                <span>·</span><span>{fmt(r.at)}</span>
                {r.old && <Button size="sm" variant="ghost" className="ml-auto h-7 gap-1" disabled={busy === r.id} onClick={() => revert(r)} data-testid={`tr-history-revert-${r.id}`}>
                  {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />} Rétablir l'ancienne
                </Button>}
              </div>
              <div className="text-xs text-muted-foreground mb-1">Texte français : <span className="text-foreground">{r.key}</span></div>
              <div className="grid md:grid-cols-2 gap-2">
                <div className="rounded bg-destructive/5 px-2 py-1.5"><span className="text-[10px] uppercase text-muted-foreground block">Avant</span><span className="line-through decoration-destructive/50">{r.old || "(vide)"}</span></div>
                <div className="rounded bg-primary/5 px-2 py-1.5"><span className="text-[10px] uppercase text-muted-foreground block">Après</span>{r.new || "(vide)"}</div>
              </div>
            </div>
          ))}
          {!shown.length && <div className="text-sm text-muted-foreground py-8 text-center" data-testid="tr-history-empty">Aucune correction enregistrée pour l'instant.</div>}
        </div>
      )}
    </div>
  );
};
